import styled from 'styled-components';

/* ════════════════════════════════════════════════════════════════════════════
   PAGE LAYOUT
   ════════════════════════════════════════════════════════════════════════════ */

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 1px 3px ${({ theme }) => theme.colors.cardShadow};
`;

export const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
  z-index: 10;
  position: relative;
  background: ${({ theme }) => theme.colors.white};
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.tertiary};
`;

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.gray};
  cursor: pointer;
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 2px;

  &:hover {
    background: ${({ theme }) => theme.colors.hoverBg};
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const PageTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark};
`;

export const ContentSection = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  position: relative;
  z-index: 1;

  /* These flex overrides are required for full-page scroll behavior */
  .ant-tabs {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  .ant-tabs-nav {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .ant-tabs-content-holder {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .ant-tabs-content,
  .ant-tabs-tabpane {
    height: 100%;
    min-height: 0;
  }

  .ant-tabs-tabpane-active {
    display: flex;
    flex-direction: column;
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   BANNERS
   ════════════════════════════════════════════════════════════════════════════ */

export const WarningBannerStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.warningBg};
  border: 1px solid ${({ theme }) => theme.colors.warningBorder};
  margin-bottom: 14px;
  flex-shrink: 0;
`;

export const WarningIcon = styled.span`
  color: ${({ theme }) => theme.colors.warningBorder};
  font-size: 15px;
  flex-shrink: 0;
`;

export const WarningText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.warningMessage};
  font-weight: 500;
`;

/* ════════════════════════════════════════════════════════════════════════════
   UPLOAD RESULT BADGE
   ════════════════════════════════════════════════════════════════════════════ */

export const UploadResultBadgeStyled = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: ${({ $hasFailures, theme }) =>
    $hasFailures ? theme.colors.warningBg : theme.colors.successBg};
  border: 1px solid ${({ $hasFailures, theme }) =>
    $hasFailures ? theme.colors.warningBorder : theme.colors.successBorder};
  color: ${({ $hasFailures, theme }) =>
    $hasFailures ? theme.colors.warningMessage : theme.colors.statusCompleted};
`;

export const UploadFailedCount = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.warningMessage};
`;

export const UploadDismissIcon = styled.span`
  cursor: pointer;
  font-size: 10px;
  opacity: 0.6;
  margin-left: 2px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   TABLE WRAPPER — shared by both Compensation & Currency tables
   ════════════════════════════════════════════════════════════════════════════ */

export const CompTableWrapperStyled = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;

  .ant-spin-nested-loading,
  .ant-spin-container,
  .ant-table-wrapper,
  .ant-table,
  .ant-table-container {
    min-height: 0;
  }

  .ant-table-thead > tr > th {
    white-space: normal !important;
  }

  .ant-table-tbody > tr.comp-editing-row > td {
    background: ${({ theme }) => theme.colors.warningBg};
  }

  .ant-table-tbody > tr.comp-bulk-updated-row > td {
    background-color: ${({ theme }) => theme.colors.successBgStrong} !important;
  }

  .ant-table-pagination {
    position: sticky;
    bottom: 0;
    background: ${({ theme }) => theme.colors.white};
    padding: 12px 16px;
    margin: 0 !important;
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   SOURCE BADGE
   ════════════════════════════════════════════════════════════════════════════ */

export const SourceBadgeStyled = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
  color: ${({ $source, theme }) => {
    if ($source === 'ACTUAL') return theme.colors.statusCompleted;
    if ($source === 'AI_GENERATED') return theme.colors.info;
    return theme.colors.statusFailed;
  }};
  background: ${({ $source, theme }) => {
    if ($source === 'ACTUAL') return theme.colors.successBg;
    if ($source === 'AI_GENERATED') return theme.colors.infoBg;
    return theme.colors.warningBg;
  }};
`;

/* ════════════════════════════════════════════════════════════════════════════
   SEARCH
   ════════════════════════════════════════════════════════════════════════════ */

export const CompSearchWrapperStyled = styled.div`
  min-width: 260px;

  .ant-select {
    width: 100%;
  }

  /*
   * Ant Design places both allowClear (×) and suffixIcon in the same right-side
   * slot. When suffixIcon is an SbButton the × renders on top of it.
   * Offsetting .ant-select-clear pushes it left of the button so both are visible.
   */
  .ant-select-clear {
    right: 44px;
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   INLINE EDIT
   ════════════════════════════════════════════════════════════════════════════ */

export const EditCellWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const EditIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.tertiary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.hoverBg};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   CURRENCY SETTINGS
   ════════════════════════════════════════════════════════════════════════════ */

export const CurrencyHeaderCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  margin: 16px 0;
  flex-shrink: 0;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.light};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-wrap: wrap;
  gap: 12px;
`;

export const CurrencyStatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

export const CurrencyStat = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray};

  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.dark};
  }
`;

export const CurrencyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.secondary};
`;

export const CurrencyActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const LegalEntityCell = styled.div`
  display: flex;
  flex-direction: column;

  .legal-entity-name {
    font-weight: 600;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.dark};
  }

  .legal-entity-country {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.tertiary};
  }
`;

export const ConversionRateText = styled.span`
  strong {
    font-weight: 700;
    font-size: 15px;
  }
`;

// Block + own ellipsis so the tooltip anchors to the visible cell, not the full text
export const EllipsisCell = styled.span`
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const UpdatedByCell = styled.div`
  line-height: 1.4;
`;

export const UpdatedByEmail = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.dark};
`;

export const UpdatedByDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.tertiary};
`;

export const FxRateInput = styled.div`
  .ant-input-number {
    width: 160px;
  }
`;

export const OpYearInput = styled.div`
  .ant-input {
    width: 100px;
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   TOOLBAR & BUTTONS
   ════════════════════════════════════════════════════════════════════════════ */

export const BulkToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const PremiumButtonWrapper = styled.div`
  display: inline-flex;

  .ant-btn {
    border-radius: 10px !important;
    font-weight: 600 !important;
    padding: 6px 20px !important;
    height: auto !important;
    font-size: 13px !important;
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
   TAB CONTENT & EMPTY STATE
   ════════════════════════════════════════════════════════════════════════════ */

export const TabContentWrapper = styled.div`
  height: 100%;
  min-height: 0;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const EmptyStateWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
`;
